// Comandos do agregado Cliente — cadastro/edição do Cliente com suas Paradas
// (Rotina de Coleta e Devolução) e os Tipos de Peça visíveis, tudo numa
// transição só (é assim que a tela grava). Dado puro; o executor só chama o
// domínio. `id` no `salvar` de um Cliente novo vem do cliente (ADR-0003).

import type {
  BaseFaturamento,
  CicloFechamento,
  Snapshot,
} from "../domain/snapshot.ts";
import {
  criarCliente,
  definirAtivoCliente,
  editarCliente,
  type DadosCliente,
} from "../domain/clientes.ts";
import type { DadosParada } from "../domain/paradas.ts";
import { assertNunca } from "../lib/assert-nunca.ts";
import {
  comoObjeto,
  exigirBooleano,
  exigirLista,
  lista,
  exigirTexto,
  paraNumero,
  texto,
  umDe,
} from "./_forma.ts";
import type { ContextoComando } from "./lotes.ts";

// Os comandos referenciam os tipos do domínio (`DadosCliente`, `DadosParada`)
// em vez de cópias paralelas: um campo novo no domínio vira erro de compilação
// aqui, em vez de chegar `undefined` por um `as` (ADR-0013).
interface DadosSalvar {
  id: string;
  dados: DadosCliente;
}

export type ComandoCliente =
  // `criar` e `editar` gravam o Cliente + Paradas + Tipos visíveis numa
  // transição só; `paradas` e `tiposPecaVisiveis` dentro de `dados` são o
  // estado COMPLETO (não é atualização parcial). `criar` traz o `id` do
  // cliente (ADR-0003).
  | ({ tipo: "criar" } & DadosSalvar)
  | ({ tipo: "editar" } & DadosSalvar)
  | { tipo: "definirAtivo"; id: string; ativo: boolean };

export function executarComandoCliente(
  snapshot: Snapshot,
  cmd: ComandoCliente,
  _ctx: ContextoComando,
): Snapshot {
  switch (cmd.tipo) {
    case "criar":
      return criarCliente(snapshot, cmd.dados, cmd.id);
    case "editar":
      return editarCliente(snapshot, cmd.id, cmd.dados);
    case "definirAtivo":
      return definirAtivoCliente(snapshot, cmd.id, cmd.ativo);
    default:
      return assertNunca(cmd);
  }
}

// --- Validação da forma do comando (fronteira HTTP) -------------------------

function validarCiclo(valor: unknown): CicloFechamento {
  const o = comoObjeto(valor, "Ciclo de fechamento");
  const tipo = umDe(o.tipo, "cicloFechamento.tipo", [
    "mensal",
    "quinzenal",
    "customizado",
  ]);
  if (tipo === "customizado") {
    return {
      tipo,
      diaInicio: paraNumero(o.diaInicio),
      diaFim: paraNumero(o.diaFim),
    };
  }
  return { tipo };
}

const ACOES_PARADA = ["coletar", "devolver", "ambos"] as const;

function validarParada(valor: unknown, i: number): DadosParada {
  const o = comoObjeto(valor, `Parada #${i + 1}`);
  return {
    diasSemana: exigirLista(o.diasSemana, `paradas[${i}].diasSemana`, (d) =>
      paraNumero(d),
    ),
    horario: texto(o.horario),
    acao: umDe(o.acao, `paradas[${i}].acao`, ACOES_PARADA),
  };
}

function validarTiposVisiveis(valor: unknown): string[] | null {
  if (valor === null) return null;
  // `undefined`/qualquer outra coisa é erro — o comando grava o estado
  // COMPLETO, não faz atualização parcial.
  if (!Array.isArray(valor)) {
    throw new Error("`tiposVisiveis` precisa ser uma lista ou null.");
  }
  return valor.map((x, i) => exigirTexto(x, `tiposVisiveis[${i}]`));
}

function validarDados(valor: unknown): DadosCliente {
  const o = comoObjeto(valor, "Dados do cliente");
  return {
    nome: texto(o.nome),
    endereco: texto(o.endereco),
    contato: texto(o.contato),
    codigo: texto(o.codigo),
    tipo: texto(o.tipo),
    baseFaturamento: umDe(o.baseFaturamento, "baseFaturamento", [
      "entrada",
      "saida",
    ]),
    cicloFechamento: validarCiclo(o.cicloFechamento),
    // fronteira crua: o domínio normaliza para boolean (padrão: não) — ADR-0014
    devolucaoNoMesmoDia: o.devolucaoNoMesmoDia === true,
    // números crus: o domínio recusa NaN/fora de faixa com mensagem amigável
    diasVencimento: lista(o.diasVencimento, "diasVencimento", (d) => paraNumero(d)),
    // `paradas` e `tiposPecaVisiveis` vêm dentro de `dados` (estado COMPLETO,
    // não atualização parcial) e o domínio os aplica no cadastro/edição.
    paradas: exigirLista(o.paradas, "paradas", validarParada),
    tiposPecaVisiveis: validarTiposVisiveis(o.tiposPecaVisiveis),
  };
}

function validarSalvar(c: Record<string, unknown>): DadosSalvar {
  return {
    id: exigirTexto(c.id, "id"),
    dados: validarDados(c.dados),
  };
}

export function validarComandoCliente(valor: unknown): ComandoCliente {
  const c = comoObjeto(valor, "Comando de cliente");
  switch (c.tipo) {
    case "criar":
      return { tipo: "criar", ...validarSalvar(c) };
    case "editar":
      return { tipo: "editar", ...validarSalvar(c) };
    case "definirAtivo":
      return {
        tipo: "definirAtivo",
        id: exigirTexto(c.id, "id"),
        ativo: exigirBooleano(c.ativo, "ativo"),
      };
    default:
      throw new Error(`Tipo de comando de cliente desconhecido: ${String(c.tipo)}.`);
  }
}
